import { create } from 'zustand';
import { startTransition } from 'react';
import api, { extractData } from '../utils/api';
import { playNotificationSound, showNewEmailNotification } from '../services/notificationService';
import { DEMO_EMAILS, DEMO_SENT_EMAILS, DEMO_DRAFT_EMAILS, DEMO_SPAM_EMAILS, DEMO_TRASH_EMAILS } from '../utils/demoMode';

// Track known email IDs across fetch methods to detect truly new arrivals
let _knownEmailIds = new Set<string>();
let _initialLoadDone = false;
// Timestamp of when this session's initial load completed.
// Only emails with receivedAt AFTER this timestamp are considered "new" for notifications.
let _sessionLoginTime = 0;

// Reset tracking state on logout so re-login gets a clean slate
export function resetMailTracking() {
  _knownEmailIds = new Set<string>();
  _initialLoadDone = false;
  _sessionLoginTime = 0;
}

// Show a native DOM notification toast (bypasses React rendering entirely)
function _showNewEmailToast(email: any, count: number) {
  // Remove any existing toast
  const existing = document.getElementById('cruvz-new-email-toast');
  if (existing) existing.remove();

  const fromStr = typeof email.from === 'string'
    ? email.from
    : email.fromName || email.from?.name || email.from?.address || 'Unknown';
  const subject = email.subject || '(no subject)';

  const toast = document.createElement('div');
  toast.id = 'cruvz-new-email-toast';

  // Build DOM elements safely (no innerHTML with user data)
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;align-items:center;gap:12px;';

  const iconWrap = document.createElement('div');
  iconWrap.style.cssText = 'width:36px;height:36px;border-radius:50%;background:rgba(26,115,232,0.1);display:flex;align-items:center;justify-content:center;flex-shrink:0;';
  iconWrap.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="#1a73e8"/></svg>';

  const content = document.createElement('div');
  content.style.cssText = 'flex:1;min-width:0;';

  const title = document.createElement('div');
  title.style.cssText = 'font-weight:600;font-size:13px;color:#1a1a1a;margin-bottom:2px;';
  title.textContent = count === 1 ? 'New email received' : `${count} new emails received`;

  const detail = document.createElement('div');
  detail.style.cssText = 'font-size:12px;color:#666;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
  detail.textContent = fromStr + (subject ? ' \u2014 ' + subject : '');

  content.appendChild(title);
  content.appendChild(detail);

  const closeBtn = document.createElement('div');
  closeBtn.style.cssText = 'cursor:pointer;padding:4px;color:#999;font-size:18px;line-height:1;';
  closeBtn.textContent = '\u00d7';

  row.appendChild(iconWrap);
  row.appendChild(content);
  row.appendChild(closeBtn);
  toast.appendChild(row);
  Object.assign(toast.style, {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: '99999',
    background: '#ffffff',
    border: '1px solid rgba(26,115,232,0.2)',
    borderRadius: '10px',
    padding: '14px 16px',
    minWidth: '320px',
    maxWidth: '420px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    cursor: 'pointer',
    opacity: '0',
    transform: 'translateY(20px)',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
  });

  document.body.appendChild(toast);

  // Force browser to render at opacity:0 first, then animate in (double rAF)
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translateY(0)';
    });
  });

  // Close button
  closeBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(20px)';
    setTimeout(() => toast.remove(), 300);
  });

  // Auto-dismiss after 15 seconds (matches Outlook/Zoho duration)
  setTimeout(() => {
    if (document.getElementById('cruvz-new-email-toast')) {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(20px)';
      setTimeout(() => toast.remove(), 300);
    }
  }, 15000);
}

// Expose test function globally for debugging
(window as any).__testNotification = () => {
  _showNewEmailToast({ from: 'dev@cruvz.in', subject: 'Test notification — this is working!' }, 1);
  try { playNotificationSound('chime'); } catch (_e) { /* ignore */ }
};

export type FollowUpTag = 'none' | 'reply-needed' | 'waiting-response' | 'action-required' | 'follow-up' | 'delegated';

export interface Email {
  id: string;
  messageId: string;
  subject: string;
  from: EmailAddress | string;
  to: EmailAddress[] | string;
  cc?: EmailAddress[] | string;
  bcc?: EmailAddress[] | string;
  body: string;
  htmlBody?: string;
  textBody?: string;
  preheader?: string;
  date: string;
  receivedAt?: string;
  createdAt?: string;
  isRead: boolean;
  isStarred: boolean;
  isFlagged: boolean;
  isImportant: boolean;
  isDraft: boolean;
  isPinned?: boolean;
  isSnoozed?: boolean;
  snoozedUntil?: string;
  scheduledAt?: Date | null;
  scheduled?: boolean;
  requestReadReceipt?: boolean;
  reminderAt?: string;
  followUpTag?: FollowUpTag;
  labels: string[];
  folder: string;
  attachments: Attachment[];
  threadId?: string;
  replyTo?: string;
  inReplyTo?: string;
  references?: string[];
  headers?: Record<string, string>;
  // Delivery tracking
  deliveryStatus?: 'none' | 'delivered' | 'partial' | 'failed' | 'pending';
  deliveredAt?: string;
  smtpResponse?: string;
  deliveryDetails?: {
    accepted?: string[];
    rejected?: string[];
    pending?: string[];
    response?: string;
    messageSize?: number;
    envelopeFrom?: string;
    envelopeTo?: string;
    tls?: boolean;
    timestamp?: string;
    error?: string;
    code?: string;
  };
}

export interface EmailAddress {
  name?: string;
  address: string;
}

export interface Attachment {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  contentId?: string;
  contentDisposition?: string;
  isInline?: boolean;
  url?: string;
}

// For sending emails with attachments
export interface SendAttachment {
  file: File;
  filename: string;
}

export interface Folder {
  id: string;
  name: string;
  type: 'system' | 'custom';
  icon?: string;
  unreadCount: number;
  totalCount: number;
  color?: string;
  parentId?: string;
  children?: Folder[];
}

export interface Label {
  id: string;
  name: string;
  color: string;
}

interface SearchFilters {
  from?: string;
  to?: string;
  subject?: string;
  hasAttachment?: boolean;
  isStarred?: boolean;
  isUnread?: boolean;
  folder?: string;
  label?: string;
  dateFrom?: Date | null;
  dateTo?: Date | null;
}

// New interfaces for Zoho-like features
export interface ScheduledEmail {
  id: string;
  fromAddress: string;
  toAddresses: string[];
  ccAddresses?: string[];
  bccAddresses?: string[];
  subject: string;
  textBody?: string;
  htmlBody?: string;
  attachments?: any[];
  scheduledAt: Date;
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  sentAt?: Date;
  errorMessage?: string;
  createdAt: Date;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject?: string;
  textBody?: string;
  htmlBody?: string;
  isDefault: boolean;
  useCount: number;
  createdAt: Date;
}

export interface EmailSignature {
  id: string;
  name: string;
  htmlContent: string;
  textContent?: string;
  forAddress?: string;
  isDefault: boolean;
  includeInReplies: boolean;
  includeInForwards: boolean;
  createdAt: Date;
}

export interface VacationResponder {
  id: string;
  isEnabled: boolean;
  subject: string;
  htmlBody: string;
  textBody?: string;
  startDate?: Date;
  endDate?: Date;
  replyToContactsOnly: boolean;
  replyOncePerSender: boolean;
  excludeAddresses?: string[];
}

export interface SavedSearch {
  id: string;
  name: string;
  query?: string;
  filters?: SearchFilters;
  useCount: number;
  lastUsedAt?: Date;
}

export interface EmailReminder {
  id: string;
  emailId: string;
  remindAt: Date;
  note?: string;
  isCompleted: boolean;
  notificationSent: boolean;
}

interface MailState {
  emails: Email[];
  selectedEmail: Email | null;
  selectedEmails: string[];
  folders: Folder[];
  labels: Label[];
  currentFolder: string;
  searchQuery: string;
  searchScope: 'all' | 'folder';
  searchSort: 'date' | 'relevance';
  includeSpamTrash: boolean;
  searchFilters: SearchFilters;
  inboxCategory: 'all' | 'primary' | 'social' | 'promotions' | 'updates' | 'forums';
  isLoading: boolean;
  error: string | null;
  
  // Pagination
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  // nextCursor: opaque cursor returned by the backend for infinite-scroll. The Stalwart
  // backend returns a numeric offset (position) cursor; the Postgres backend uses a keyset
  // cursor (cursorDate + cursorId). We store whichever the backend returned so the next
  // loadMoreEmails() call can pass it straight back instead of re-deriving it from the last
  // email's UUID (which breaks Stalwart's numeric-offset pagination and caused an infinite
  // "Loading more emails..." loop returning the same first page forever).
  nextCursor: string | null;
  // cursorHistory[i] = the cursor used to fetch page (i+1); see nextPage/prevPage.
  cursorHistory: ({ cursorDate: string; cursorId: string } | null)[];
  
  // Compose
  draftEmail: Partial<Email> | null;
  isComposeOpen: boolean;
  
  // Scheduled Emails
  scheduledEmails: ScheduledEmail[];
  
  // Templates
  templates: EmailTemplate[];
  
  // Signatures
  signatures: EmailSignature[];
  defaultSignature: EmailSignature | null;
  
  // Vacation Responder
  vacationResponder: VacationResponder | null;
  
  // Saved Searches
  savedSearches: SavedSearch[];
  
  // Reminders
  reminders: EmailReminder[];
  
  // New email notification
  newEmailNotification: { count: number; latestEmail: any } | null;
  clearNewEmailNotification: () => void;

  // Actions
  loadDemoEmails: (folder: string) => void;
  loadDemoFolders: () => void;
  fetchEmails: (folder?: string, page?: number, cursor?: { cursorDate: string; cursorId: string }) => Promise<void>;
  fetchEmailsSilent: (folder?: string) => Promise<void>;
  fetchEmail: (id: string) => Promise<void>;
  fetchFolders: () => Promise<void>;
  fetchLabels: () => Promise<void>;
  syncEmails: () => Promise<void>;
  
  // Pagination helpers
  nextPage: () => Promise<void>;
  prevPage: () => Promise<void>;
  goToPage: (page: number) => Promise<void>;
  loadMoreEmails: () => Promise<void>;
  
  setSelectedEmail: (email: Email | null) => void;
  toggleEmailSelection: (id: string) => void;
  selectAllEmails: () => void;
  clearSelection: () => void;
  
  markAsRead: (ids: string[], isRead: boolean) => Promise<void>;
  markAsStarred: (ids: string[], isStarred: boolean) => Promise<void>;
  markAsImportant: (ids: string[], isImportant: boolean) => Promise<void>;
  pinEmail: (ids: string[], isPinned: boolean) => Promise<void>;
  snoozeEmails: (ids: string[], until: Date) => Promise<void>;
  setReminder: (ids: string[], reminderAt: Date | null) => Promise<void>;
  setFollowUpTag: (ids: string[], tag: FollowUpTag) => Promise<void>;
  moveToFolder: (ids: string[], folder: string) => Promise<void>;
  markAsSpam: (ids: string[]) => Promise<void>;
  markAsNotSpam: (ids: string[]) => Promise<void>;
  deleteEmails: (ids: string[]) => Promise<void>;
  addLabel: (ids: string[], labelId: string) => Promise<void>;
  removeLabel: (ids: string[], labelId: string) => Promise<void>;
  
  sendEmail: (email: Partial<Email>) => Promise<void>;
  scheduleEmail: (email: Partial<Email>, scheduledAt: Date) => Promise<void>;
  saveDraft: (email: Partial<Email>) => Promise<void>;
  
  // Scheduled Email Actions
  fetchScheduledEmails: () => Promise<void>;
  cancelScheduledEmail: (id: string) => Promise<void>;
  updateScheduledEmail: (id: string, data: Partial<ScheduledEmail>) => Promise<void>;
  
  // Template Actions
  fetchTemplates: () => Promise<void>;
  createTemplate: (template: Partial<EmailTemplate>) => Promise<void>;
  updateTemplate: (id: string, template: Partial<EmailTemplate>) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  
  // Signature Actions
  fetchSignatures: () => Promise<void>;
  createSignature: (signature: Partial<EmailSignature>) => Promise<void>;
  updateSignature: (id: string, signature: Partial<EmailSignature>) => Promise<void>;
  deleteSignature: (id: string) => Promise<void>;
  setDefaultSignature: (id: string) => Promise<void>;
  getSignatureForAddress: (address: string) => Promise<EmailSignature | null>;
  
  // Vacation Responder Actions
  fetchVacationResponder: () => Promise<void>;
  updateVacationResponder: (data: Partial<VacationResponder>) => Promise<void>;
  toggleVacationResponder: (enabled: boolean) => Promise<void>;
  
  // Saved Search Actions
  fetchSavedSearches: () => Promise<void>;
  createSavedSearch: (search: Partial<SavedSearch>) => Promise<void>;
  deleteSavedSearch: (id: string) => Promise<void>;
  executeSavedSearch: (id: string) => Promise<void>;
  
  // Reminder Actions
  fetchReminders: () => Promise<void>;
  createReminder: (emailId: string, remindAt: Date, note?: string) => Promise<void>;
  deleteReminder: (id: string) => Promise<void>;
  completeReminder: (id: string) => Promise<void>;
  
  setCurrentFolder: (folder: string) => void;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;
  setSearchScope: (scope: 'all' | 'folder') => void;
  setSearchSort: (sort: 'date' | 'relevance') => void;
  setIncludeSpamTrash: (v: boolean) => void;
  setSearchFilters: (filters: SearchFilters) => void;
  setInboxCategory: (category: 'all' | 'primary' | 'social' | 'promotions' | 'updates' | 'forums') => void;
  setComposeOpen: (isOpen: boolean, draft?: Partial<Email> | null) => void;
  
  createFolder: (name: string, parentId?: string, color?: string) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  renameFolder: (id: string, name: string, color?: string) => Promise<void>;
  
  createLabel: (name: string, color: string) => Promise<void>;
  deleteLabel: (id: string) => Promise<void>;
  updateLabel: (id: string, name: string, color: string) => Promise<void>;
}

export const useMailStore = create<MailState>((set, get) => ({
  emails: [],
  selectedEmail: null,
  selectedEmails: [],
  folders: [
    { id: 'inbox', name: 'Inbox', type: 'system', icon: 'inbox', unreadCount: 0, totalCount: 0 },
    { id: 'starred', name: 'Starred', type: 'system', icon: 'star', unreadCount: 0, totalCount: 0 },
    { id: 'snoozed', name: 'Snoozed', type: 'system', icon: 'schedule', unreadCount: 0, totalCount: 0 },
    { id: 'important', name: 'Important', type: 'system', icon: 'label_important', unreadCount: 0, totalCount: 0 },
    { id: 'sent', name: 'Sent', type: 'system', icon: 'send', unreadCount: 0, totalCount: 0 },
    { id: 'drafts', name: 'Drafts', type: 'system', icon: 'drafts', unreadCount: 0, totalCount: 0 },
    { id: 'spam', name: 'Spam', type: 'system', icon: 'report', unreadCount: 0, totalCount: 0 },
    { id: 'trash', name: 'Trash', type: 'system', icon: 'delete', unreadCount: 0, totalCount: 0 },
    { id: 'archive', name: 'Archive', type: 'system', icon: 'archive', unreadCount: 0, totalCount: 0 },
    { id: 'outbox', name: 'Outbox', type: 'system', icon: 'outbox', unreadCount: 0, totalCount: 0 },
    { id: 'templates', name: 'Templates', type: 'system', icon: 'description', unreadCount: 0, totalCount: 0 },
  ],
  labels: [],
  currentFolder: 'inbox',
  searchQuery: '',
  searchScope: 'all' as 'all' | 'folder',
  searchSort: 'date' as 'date' | 'relevance',
  includeSpamTrash: false,
  searchFilters: {},
  inboxCategory: 'all' as 'all' | 'primary' | 'social' | 'promotions' | 'updates' | 'forums',
  isLoading: false,
  error: null,
  
  page: 1,
  limit: 100,
  total: 0,
  hasMore: false,
  isLoadingMore: false,
  nextCursor: null,
  // PERF FIX 2026-08-09 (audit finding: classic Prev/Next pagination arrows bypass cursor
  // pagination and use slow OFFSET, degrading as scroll depth grows -- see nextPage/prevPage
  // below). cursorHistory[i] = the cursor used to fetch page (i+1); index 0 is always null
  // since page 1 needs no cursor. Reset to [null] whenever a fresh (non-cursor) page-1 load
  // happens (folder switch, search, refresh).
  cursorHistory: [null] as ({ cursorDate: string; cursorId: string } | null)[],
  
  draftEmail: null,
  isComposeOpen: false,
  
  // New feature states
  scheduledEmails: [],
  templates: [],
  signatures: [],
  defaultSignature: null,
  vacationResponder: null,
  savedSearches: [],
  reminders: [],

  // New email notification
  newEmailNotification: null,
  clearNewEmailNotification: () => set({ newEmailNotification: null }),

  loadDemoEmails: (folder: string) => {
    const folderEmails =
      folder === 'inbox'     ? DEMO_EMAILS
      : folder === 'sent'    ? DEMO_SENT_EMAILS
      : folder === 'drafts'  ? DEMO_DRAFT_EMAILS
      : folder === 'spam'    ? DEMO_SPAM_EMAILS
      : folder === 'trash'   ? DEMO_TRASH_EMAILS
      : folder === 'starred' ? DEMO_EMAILS.filter((e: any) => e.isStarred)
      : folder === 'flagged' ? DEMO_EMAILS.filter((e: any) => e.isFlagged)
      : folder === 'important' ? DEMO_EMAILS.filter((e: any) => e.isImportant)
      : folder === 'unread'  ? DEMO_EMAILS.filter((e: any) => !e.isRead)
      : folder === 'all'     ? [...DEMO_EMAILS, ...DEMO_SENT_EMAILS, ...DEMO_DRAFT_EMAILS]
      : [];
    set({ emails: folderEmails as any, total: folderEmails.length, isLoading: false, error: null, page: 1, currentFolder: folder });
  },

  loadDemoFolders: () => {
    const demoCounts: Record<string, { unread: number; total: number }> = {
      inbox:   { unread: 12, total: 12 },
      sent:    { unread: 5,  total: 5  },
      drafts:  { unread: 3,  total: 3  },
      spam:    { unread: 4,  total: 4  },
      trash:   { unread: 3,  total: 3  },
      starred: { unread: 1,  total: 1  },
      snoozed: { unread: 0,  total: 0  },
      important: { unread: 4, total: 4 },
      archive: { unread: 0,  total: 0  },
    };
    const systemFolders = get().folders.filter(f => f.type === 'system').map(f => ({
      ...f,
      unreadCount: demoCounts[f.id]?.unread ?? f.unreadCount,
      totalCount:  demoCounts[f.id]?.total  ?? f.totalCount,
    }));
    const specialViews = [
      { id: 'unread',  name: 'Unread',       type: 'system' as const, icon: 'mark_email_unread', unreadCount: 4,  totalCount: 4  },
      { id: 'all',     name: 'All messages', type: 'system' as const, icon: 'all_inbox',          unreadCount: 20, totalCount: 20 },
      { id: 'flagged', name: 'Flagged',      type: 'system' as const, icon: 'flag',               unreadCount: 1,  totalCount: 1  },
    ];
    set({ folders: [...systemFolders, ...specialViews] });
  },

  fetchEmails: async (folder?: string, page: number = 1, cursor?: { cursorDate: string; cursorId: string }) => {
    const currentFolder = folder || get().currentFolder;
    // Update currentFolder immediately so the stale-response guard below
    // works correctly on initial page load (store default is 'inbox' but URL
    // may be '/mail/spam' — without this, the guard would discard valid results).
    // A genuine fresh page-1 load (no cursor supplied) resets the cursor history so a later
    // nextPage() rebuilds it from scratch, rather than reusing stale cursors from a previous
    // folder/search.
    set({
      isLoading: true,
      error: null,
      emails: [],
      total: 0,
      currentFolder,
      ...(page === 1 && !cursor ? { cursorHistory: [null] } : {}),
    });
    
    try {
      const searchQuery = get().searchQuery || '';
      const searchFilters = get().searchFilters || {};
      
      
      // Parse search operators from query string
      const parseSearchQuery = (query: string) => {
        const result: Record<string, string> = {};
        const knownOperators = ['from', 'to', 'tocc', 'cc', 'subject', 'body', 'contains', 'folder', 'label', 'attachment', 'after', 'before'];

        const normalizeOperatorChains = (rawQuery: string) => {
          let normalized = rawQuery || '';

          normalized = normalized
            .replace(/\bto\s*\/\s*cc\s*:/gi, 'tocc:')
            .replace(/\bto\s*-\s*cc\s*:/gi, 'tocc:')
            .replace(/\bto\s*&\s*cc\s*:/gi, 'tocc:')
            .replace(/\bin\s+folder:/gi, 'folder:')
            .replace(/\bfrom\s+date:/gi, 'after:')
            .replace(/\bto\s+date:/gi, 'before:')
            .replace(/\bwith\s+attachment\s*:/gi, 'has:attachment')
            .replace(/\bunread\s*:/gi, 'is:unread')
            .replace(/\bstarred\s*:/gi, 'is:starred');

          normalized = normalized.replace(/\b([a-z/]+):([a-z/]+):([^\s"]+)/gi, (full, first, second, value) => {
            const firstOp = String(first).toLowerCase();
            const secondOp = String(second).toLowerCase();
            if (knownOperators.includes(firstOp) && knownOperators.includes(secondOp)) {
              return `${secondOp}:${value}`;
            }
            return full;
          });

          return normalized;
        };

        let remainingQuery = normalizeOperatorChains(query);

        const resolveRelativeDate = (rawValue: string) => {
          const value = rawValue.trim().toLowerCase();
          const now = new Date();
          const normalizedNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());

          const toIsoDate = (date: Date) => date.toISOString();

          if (!value) return rawValue;
          if (!Number.isNaN(Date.parse(value))) return new Date(value).toISOString();

          if (value === 'today') return toIsoDate(normalizedNow);
          if (value === 'yesterday') {
            const date = new Date(normalizedNow);
            date.setDate(date.getDate() - 1);
            return toIsoDate(date);
          }
          if (value === 'this week') {
            const date = new Date(normalizedNow);
            const day = date.getDay();
            const diff = day === 0 ? -6 : 1 - day;
            date.setDate(date.getDate() + diff);
            return toIsoDate(date);
          }
          if (value === 'last week') {
            const date = new Date(normalizedNow);
            const day = date.getDay();
            const diff = day === 0 ? -6 : 1 - day;
            date.setDate(date.getDate() + diff - 7);
            return toIsoDate(date);
          }
          if (value === 'this month') {
            return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
          }
          if (value === 'last month') {
            return toIsoDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
          }

          return rawValue;
        };

        const extractOperatorValue = (source: string, operator: string) => {
          const pattern = new RegExp(`(?:^|\\s)${operator}:\\s*(?:"([^"]+)"|(\\S+))`, 'i');
          const match = pattern.exec(source);
          if (!match) {
            return { value: undefined, nextQuery: source };
          }

          return {
            value: (match[1] || match[2] || '').trim(),
            nextQuery: source.replace(match[0], ' ').replace(/\s+/g, ' ').trim(),
          };
        };
        
        [
          ['from', 'from'],
          ['to', 'to'],
          ['tocc', 'to'],
          ['cc', 'to'],
          ['subject', 'subject'],
          ['body', 'body'],
          ['contains', 'search'],
          ['folder', 'folderType'],
          ['label', 'label'],
          ['attachment', 'attachmentName'],
          ['larger', 'sizeGt'],
          ['smaller', 'sizeLt'],
        ].forEach(([operator, key]) => {
          const extracted = extractOperatorValue(remainingQuery, operator);
          if (extracted.value) {
            if (key === 'search' && result.search) {
              result.search = `${result.search} ${extracted.value}`.trim();
            } else {
              result[key] = extracted.value;
            }
            remainingQuery = extracted.nextQuery;
          }
        });

        ['has:attachment', 'is:starred', 'is:unread'].forEach((token) => {
          const pattern = new RegExp(`(?:^|\\s)${token}(?=\\s|$)`, 'i');
          const match = pattern.exec(remainingQuery);
          if (match) {
            if (token === 'has:attachment') result.hasAttachment = 'true';
            if (token === 'is:starred') result.isStarred = 'true';
            if (token === 'is:unread') result.isUnread = 'true';
            remainingQuery = remainingQuery.replace(match[0], ' ').replace(/\s+/g, ' ').trim();
          }
        });

        const afterExtracted = extractOperatorValue(remainingQuery, 'after');
        if (afterExtracted.value) {
          result.dateFrom = resolveRelativeDate(afterExtracted.value);
          remainingQuery = afterExtracted.nextQuery;
        }

        const beforeExtracted = extractOperatorValue(remainingQuery, 'before');
        if (beforeExtracted.value) {
          result.dateTo = resolveRelativeDate(beforeExtracted.value);
          remainingQuery = beforeExtracted.nextQuery;
        }
        
        // The remaining text is a general search
        if (remainingQuery.trim()) {
          result.search = remainingQuery.trim();
        }
        
        return result;
      };
      
      // Parse search query for operators
      const parsedSearch = parseSearchQuery(searchQuery);
      
      // Build params object - remove folder restriction when searching
      const params: Record<string, any> = {
        page,
        limit: get().limit,
      };

      // PERF FIX 2026-08-09: classic Prev/Next pagination (nextPage/prevPage) supplies a
      // cursor built from the currently-displayed page's boundary email instead of relying on
      // OFFSET, which is what made those arrows get slower the deeper a user paginated (audit
      // finding, 2026-08-08). Only applies when not searching, matching the same restriction
      // already used by loadMoreEmails/backend.
      if (cursor && !searchQuery) {
        delete params.page;
        params.cursorDate = cursor.cursorDate;
        params.cursorId = cursor.cursorId;
      }
      
  // Only apply folder filter if no search is active, OR if searchScope is 'folder'
      const searchScope = get().searchScope;
      const includeSpamTrash = get().includeSpamTrash;
      if (!searchQuery && !Object.keys(searchFilters).length) {
        params.folder = currentFolder;
      } else if (searchQuery && searchScope === 'folder') {
        params.folder = currentFolder;
      }
      if (includeSpamTrash && searchQuery) params.includeSpamTrash = 'true';

      // Special view folders are not real mailboxes — they are filtered views.
      // Pass the corresponding flag so the backend filters correctly instead of
      // returning ALL emails (which is what happened for 'starred' before).
      if (!searchQuery && !Object.keys(searchFilters).length) {
        if (currentFolder === 'starred') params.isStarred = 'true';
        else if (currentFolder === 'unread') params.isUnread = 'true';
        else if (currentFolder === 'flagged') params.isFlagged = 'true';
        else if (currentFolder === 'important') params.isImportant = 'true';
        else if (currentFolder === 'all' || currentFolder === 'all-messages') params.folder = 'all';
      }
      
      // Add parsed search params
      if (parsedSearch.search) params.search = parsedSearch.search;
      if (parsedSearch.from) params.from = parsedSearch.from;
      if (parsedSearch.to) params.to = parsedSearch.to;
      if (parsedSearch.subject) params.subject = parsedSearch.subject;
      if (parsedSearch.body) params.body = parsedSearch.body;
      if (parsedSearch.folderType) params.folderType = parsedSearch.folderType;
      if (parsedSearch.hasAttachment) params.hasAttachment = parsedSearch.hasAttachment;
      if (parsedSearch.isStarred) params.isStarred = parsedSearch.isStarred;
      if (parsedSearch.isUnread) params.isUnread = parsedSearch.isUnread;
      if (parsedSearch.dateFrom) params.dateFrom = parsedSearch.dateFrom;
      if (parsedSearch.dateTo) params.dateTo = parsedSearch.dateTo;

      // Size filters: convert human-readable to bytes
      const parseSizeToBytes = (val: string): number => {
        if (!val) return 0;
        const m = val.match(/^(\d+(?:\.\d+)?)\s*(kb|mb|gb|b)?$/i);
        if (!m) return parseInt(val) || 0;
        const n = parseFloat(m[1]);
        const unit = (m[2] || 'b').toLowerCase();
        if (unit === 'kb') return Math.round(n * 1024);
        if (unit === 'mb') return Math.round(n * 1024 * 1024);
        if (unit === 'gb') return Math.round(n * 1024 * 1024 * 1024);
        return Math.round(n);
      };
      if (parsedSearch.sizeGt) params.sizeGt = parseSizeToBytes(parsedSearch.sizeGt);
      if (parsedSearch.sizeLt) params.sizeLt = parseSizeToBytes(parsedSearch.sizeLt);

      // Sort by relevance when search is active and user selected relevance sort
      const searchSort = get().searchSort;
      if (searchQuery && searchSort === 'relevance') params.sortBy = 'relevance';

      // Smart category filter — only apply on inbox when not searching
      const inboxCategory = get().inboxCategory;
      if (!searchQuery && currentFolder === 'inbox' && inboxCategory && inboxCategory !== 'all') {
        params.category = inboxCategory;
      }

      // Thread grouping for search results
      if (searchQuery) {
        params.groupByThread = 'true';
      }
      
      // Add search filters (from advanced panel)
      if (searchFilters.from) params.from = searchFilters.from;
      if (searchFilters.to) params.to = searchFilters.to;
      if (searchFilters.subject) params.subject = searchFilters.subject;
      if (searchFilters.hasAttachment) params.hasAttachment = 'true';
      if (searchFilters.isStarred) params.isStarred = 'true';
      if (searchFilters.isUnread) params.isUnread = 'true';
      if (searchFilters.folder) params.folderType = searchFilters.folder;
      if (searchFilters.dateFrom) params.dateFrom = searchFilters.dateFrom;
      if (searchFilters.dateTo) params.dateTo = searchFilters.dateTo;
      
      // If no operators found but query exists, use as general search
      if (searchQuery && !Object.keys(parsedSearch).length) {
        params.search = searchQuery;
      }
      
      const response = await api.get('/emails', { params });

      // STALE RESPONSE GUARD: if the user navigated to another folder while
      // this request was in-flight, discard the result to prevent folder bleed.
      if (get().currentFolder !== currentFolder) {
        set({ isLoading: false });
        return;
      }
      
      const data = response.data?.data || response.data;
      const emails = Array.isArray(data) ? data : (data.data || data.emails || []);
      // Cursor responses return meta.total = -1 (sentinel: "not recomputed, keep existing") --
      // same convention as loadMoreEmails, since redoing COUNT(*) on every cursor page is
      // needless overhead once the total is already known from the initial page-1 load.
      const rawTotal = response.data?.pagination?.total ?? data.meta?.total ?? data.total;
      const total = (rawTotal === undefined || rawTotal === -1) ? get().total : rawTotal;
      
      // On first load, register all emails silently (no notifications).
      // Notifications are handled ONLY by fetchEmailsSilent (background poll).
      const pageEmails = page === 1 ? emails : [...get().emails, ...emails];
      if (!_initialLoadDone) {
        // Record when this session's inbox was first loaded — only emails
        // arriving AFTER this moment are eligible for new-mail notifications.
        _sessionLoginTime = Date.now();
      }
      pageEmails.forEach((e: any) => _knownEmailIds.add(e.id));
      _initialLoadDone = true;

      // Use startTransition so React treats this as a non-urgent update and
      // splits the re-render work across frames instead of one 5-second block.
      startTransition(() => {
        set({
          emails: emails,
          page,
          total,
          hasMore: emails.length === get().limit,
          nextCursor: null,
          isLoading: false,
          currentFolder,
        });
      });
    } catch (error: any) {
      set({
        emails: [],
        total: 0,
        error: error.response?.data?.message || 'Failed to fetch emails',
        isLoading: false,
      });
    }
  },

  fetchEmailsSilent: async (folder?: string) => {
    // Don't fire before the initial load has seeded _knownEmailIds —
    // otherwise ALL existing emails would look like new arrivals.
    if (!_initialLoadDone) {
      return;
    }
    // Snapshot the folder we intend to poll BEFORE the async call.
    const pollFolder = folder || get().currentFolder;
    try {
      const params: Record<string, any> = {
        page: 1,
        limit: get().limit,
        folder: pollFolder,
      };

      const response = await api.get('/emails', { params });

      // STALE RESPONSE GUARD: if the user navigated away while this request
      // was in-flight, the store's currentFolder no longer matches pollFolder.
      // Writing stale results would overwrite the correct folder's emails.
      if (get().currentFolder !== pollFolder) {
        return;
      }

      const data = response.data?.data || response.data;
      const rawEmails = Array.isArray(data) ? data : (data.data || data.emails || []);
      const total = response.data?.pagination?.total || data.meta?.total || data.total || rawEmails.length;

      // Preserve locally-set read/star/flag state that may not have persisted to DB yet.
      // This prevents a race where fetchEmailsSilent fires between markAsRead being called
      // and the DB write completing, causing the UI to revert to the old isRead state.
      const localEmailMap = new Map(get().emails.map((e) => [e.id, e]));
      const newEmails = rawEmails.map((serverEmail: any) => {
        const local = localEmailMap.get(serverEmail.id);
        if (!local) return serverEmail;
        return {
          ...serverEmail,
          // Keep local state if it's more "read" than what the server returned,
          // i.e. user already marked it as read but DB hasn't caught up yet.
          isRead: local.isRead || serverEmail.isRead,
          isStarred: local.isStarred !== undefined ? local.isStarred : serverEmail.isStarred,
          isFlagged: local.isFlagged !== undefined ? local.isFlagged : serverEmail.isFlagged,
          isImportant: local.isImportant !== undefined ? local.isImportant : serverEmail.isImportant,
        };
      });

      // Detect truly new emails: not yet seen AND received after this session started.
      const brandNewEmails = rawEmails.filter((e: any) => {
        if (_knownEmailIds.has(e.id)) return false;
        const emailTime = new Date(e.receivedAt || e.createdAt || e.date || 0).getTime();
        return emailTime > _sessionLoginTime;
      });

      // Register all current emails as known
      rawEmails.forEach((e: any) => _knownEmailIds.add(e.id));

      // Whenever new mail is detected during a silent poll, refresh sidebar folder counts
      if (brandNewEmails.length > 0) {
        try { get().fetchFolders(); } catch { /* non-fatal */ }
      }

      // ── CHANGE DETECTION: skip the store update (and React re-render) when
      //    nothing has actually changed. Compare IDs + read status fingerprint.
      const currentEmails = get().emails;
      const sameCount = currentEmails.length === newEmails.length;
      const sameTotal = get().total === total;
      if (sameCount && sameTotal && brandNewEmails.length === 0) {
        // Quick fingerprint: first+last ID and first 5 read states
        const oldFp = currentEmails.slice(0, 5).map((e: any) => `${e.id}:${e.isRead}`).join(',');
        const newFp = newEmails.slice(0, 5).map((e: any) => `${e.id}:${e.isRead}`).join(',');
        if (oldFp === newFp) {
          return; // Nothing changed — skip re-render entirely
        }
      }

      // BUG FIX 2026-08-08 (Gap: "loading stuck"/blank space after scrolling past 25):
      // This silent background poll (runs every 60s) only ever fetches page=1/limit=25
      // (it's just checking for new mail / read-state changes), but it used to blindly
      // REPLACE the entire accumulated infinite-scroll list with just that one page and
      // reset `page` back to 1. If the user had scrolled down and loaded pages 2/3/4+ via
      // loadMoreEmails, the very next 60s poll silently truncated their list back down to
      // 25 items (while scrollTop stayed wherever they'd scrolled to), producing a sudden
      // blank gap below the truncated content, and confusing the index-based infinite-
      // scroll trigger (which now thought it was near the end of a much shorter list,
      // or vice versa) -- explains why the symptom got worse the longer someone stayed
      // scrolled past the first page. Fix: merge the fresh page-1 data into the front of
      // the existing list instead of discarding everything beyond it, and keep `page` as
      // whatever the user had already reached via infinite scroll.
      const existingEmails = get().emails;
      const currentPage = get().page;
      let mergedEmails = newEmails;
      let mergedPage = 1;
      let mergedHasMore = newEmails.length === get().limit;
      if (existingEmails.length > newEmails.length) {
        const freshIds = new Set(newEmails.map((e: any) => e.id));
        const preservedRest = existingEmails.slice(newEmails.length).filter((e: any) => !freshIds.has(e.id));
        mergedEmails = [...newEmails, ...preservedRest];
        mergedPage = currentPage;
        mergedHasMore = get().hasMore;
      }

      // Update emails silently (single set call)
      if (brandNewEmails.length > 0 && pollFolder === 'inbox') {
        try { playNotificationSound(); } catch (e) { console.warn('[SilentPoll] Sound failed:', e); }
        _showNewEmailToast(brandNewEmails[0], brandNewEmails.length);
        const latestEmail = brandNewEmails[0];
        const fromStr = typeof latestEmail.from === 'string'
          ? latestEmail.from
          : latestEmail.fromName || latestEmail.from?.name || latestEmail.from?.address || 'Unknown';
        showNewEmailNotification({
          from: fromStr,
          subject: latestEmail.subject || '(no subject)',
          id: latestEmail.id,
        });
        set({
          emails: mergedEmails,
          total,
          hasMore: mergedHasMore,
          page: mergedPage,
          currentFolder: pollFolder,
        });
      } else {
        set({
          emails: mergedEmails,
          total,
          hasMore: mergedHasMore,
          page: mergedPage,
          currentFolder: pollFolder,
        });
      }
    } catch (err) {
      console.warn('[SilentPoll] Error:', err);
    }
  },

  fetchEmail: async (id: string) => {
    set({ isLoading: true });
    try {
      const response = await api.get(`/emails/${id}`);
      const email = response.data?.data || response.data;
      // BUG FIX 2026-08-08 (Gap: opening an email shows the WRONG body content --
      // header/subject matches the newly-clicked email but the iframe body renders a
      // PREVIOUS email's content): handleEmailClick sets `selectedEmail` optimistically
      // (header-only, from the list row) THEN calls fetchEmail(id) to get the full
      // body/htmlBody. If the user clicks email A then quickly clicks email B before A's
      // fetch resolves, A's fetchEmail() call was still in flight and could resolve AFTER
      // B's click, silently overwriting `selectedEmail` (now B, header-correct) with A's
      // full content (body/htmlBody) -- exactly the "right header, wrong body" symptom
      // reported. Guard: if the user has since selected a different email, discard this
      // now-stale response instead of applying it.
      if (get().selectedEmail?.id !== id) {
        return;
      }
      // GET /emails/:id marks as read server-side before returning.
      // Always update the list item to isRead:true so the list reflects
      // the change immediately without waiting for the next silent poll.
      set({
        selectedEmail: { ...email, isRead: true },
        isLoading: false,
        emails: get().emails.map(e => e.id === id ? { ...e, isRead: true } : e),
      });
    } catch (error: any) {
      // Stale-response guard applies to errors too -- don't show an error for an
      // email the user has already navigated away from.
      if (get().selectedEmail?.id !== id) {
        return;
      }
      set({
        error: error.response?.data?.message || 'Failed to fetch email',
        isLoading: false,
      });
    }
  },

  fetchFolders: async () => {
    try {
      // Fetch custom folders from API
      const response = await api.get('/folders');
      console.log('[fetchFolders] raw response.data:', JSON.stringify(response.data));
      
      // Use extractData helper to unwrap double-nested response
      // API returns: { success, data: { success, data: [...folders] }, timestamp }
      const extracted = extractData<any>(response);
      console.log('[fetchFolders] extracted:', JSON.stringify(extracted));
      
      // Handle all possible shapes
      let foldersArray: any[] = [];
      if (Array.isArray(extracted)) {
        foldersArray = extracted;
      } else if (extracted && Array.isArray(extracted.data)) {
        foldersArray = extracted.data;
      }
      
      const customFolders: Folder[] = foldersArray.map((f: any) => ({
        id: f.id,
        name: f.name,
        type: 'custom' as const,
        color: f.color || '#1976d2',
        parentId: f.parentId || undefined,
        unreadCount: 0,
        totalCount: 0,
      }));
      
      console.log('[fetchFolders] customFolders count:', customFolders.length, customFolders.map(f => f.name));
      
      // Try to fetch folder counts
      let counts: Record<string, any> = {};
      try {
        const countsResponse = await api.get('/folders/counts');
        const countsData = extractData<any>(countsResponse);
        counts = (countsData && typeof countsData === 'object' && !Array.isArray(countsData)) ? countsData : {};
      } catch {
        // counts endpoint may not exist - continue without counts
      }
      
      // Update system folders with counts
      const systemFolders = get().folders.filter(f => f.type === 'system').map(f => ({
        ...f,
        unreadCount: counts[f.id]?.unread || 0,
        totalCount: counts[f.id]?.total || 0,
      }));
      
      // Add special view folders from API counts (all, unread, flagged)
      const specialViews: Folder[] = [];
      if (counts['all'] || counts['all-messages']) {
        const allCount = counts['all'] || counts['all-messages'];
        specialViews.push({
          id: 'all',
          name: 'All messages',
          type: 'system' as const,
          icon: 'all_inbox',
          unreadCount: allCount.unread || 0,
          totalCount: allCount.total || 0,
        });
      }
      if (counts['unread'] || counts['all-unread']) {
        const unreadCount = counts['unread'] || counts['all-unread'];
        specialViews.push({
          id: 'unread',
          name: 'Unread',
          type: 'system' as const,
          icon: 'mark_email_unread',
          unreadCount: unreadCount.unread || 0,
          totalCount: unreadCount.total || 0,
        });
      }
      if (counts['flagged']) {
        specialViews.push({
          id: 'flagged',
          name: 'Flagged',
          type: 'system' as const,
          icon: 'flag',
          unreadCount: counts['flagged'].unread || 0,
          totalCount: counts['flagged'].total || 0,
        });
      }
      
      // Update custom folders with counts too
      const customFoldersWithCounts = customFolders.map(f => ({
        ...f,
        unreadCount: counts[f.id]?.unread || 0,
        totalCount: counts[f.id]?.total || 0,
      }));
      
      const merged = [...systemFolders, ...specialViews, ...customFoldersWithCounts];
      console.log('[fetchFolders] setting folders:', merged.length, 'system:', systemFolders.length, 'special:', specialViews.length, 'custom:', customFolders.length);
      set({ folders: merged });
    } catch (err) {
      // Keep default folders on error - endpoint may not exist yet
      console.error('[fetchFolders] ERROR:', err);
    }
  },

  fetchLabels: async () => {
    try {
      const response = await api.get('/labels');
      const data = response.data?.data || response.data;
      set({ labels: Array.isArray(data) ? data : [] });
    } catch {
      // Labels endpoint may not exist yet - ignore silently
      console.log('Labels endpoint not available');
    }
  },

  syncEmails: async () => {
    try {
      // Trigger server-side maildir sync
      await api.post('/emails/sync');
      // Then fetch the updated emails
      await get().fetchEmails(get().currentFolder, 1);
    } catch (error: any) {
      console.error('Sync failed:', error);
      // Still fetch emails even if sync fails
      await get().fetchEmails(get().currentFolder, 1);
    }
  },

  // Pagination helpers
  // PERF FIX 2026-08-09 (audit finding, 2026-08-08): these previously always called
  // fetchEmails(folder, page) which uses OFFSET (`.skip()`) pagination -- cost grows with
  // how deep the user has paginated (measured 55ms at page 1 -> 245ms at page 148). Now
  // builds a cursor from the last/first email of the currently-displayed page (same keyset
  // approach already used by loadMoreEmails for infinite-scroll) so cost stays flat
  // regardless of depth. Falls back to plain page-based fetch when searching (cursor+search
  // isn't supported, matching the existing loadMoreEmails/backend restriction) or when no
  // cursor is available yet (e.g. empty page).
  nextPage: async () => {
    const { page, hasMore, currentFolder, emails, searchQuery, cursorHistory } = get();
    if (!hasMore) return;
    if (!searchQuery && emails.length > 0) {
      const lastEmail = emails[emails.length - 1];
      const cursorDate = new Date(lastEmail.receivedAt || lastEmail.date).toISOString();
      const cursorId = lastEmail.id;
      // Record the cursor used to reach page+1 so prevPage() can look it up later.
      const nextHistory = [...cursorHistory.slice(0, page), { cursorDate, cursorId }];
      set({ cursorHistory: nextHistory });
      await get().fetchEmails(currentFolder, page + 1, { cursorDate, cursorId });
    } else {
      await get().fetchEmails(currentFolder, page + 1);
    }
  },

  prevPage: async () => {
    const { page, currentFolder, cursorHistory } = get();
    if (page <= 1) return;
    const targetPage = page - 1;
    // cursorHistory[targetPage - 1] is the cursor that was used to originally fetch
    // targetPage (index 0 is always null, since page 1 needs no cursor).
    const cursor = cursorHistory[targetPage - 1];
    if (cursor) {
      await get().fetchEmails(currentFolder, targetPage, cursor);
    } else {
      await get().fetchEmails(currentFolder, targetPage);
    }
  },

  goToPage: async (targetPage: number) => {
    const { currentFolder, total, limit } = get();
    const maxPage = Math.ceil(total / limit);
    if (targetPage >= 1 && targetPage <= maxPage) {
      await get().fetchEmails(currentFolder, targetPage);
    }
  },

  // Infinite scroll: fetch the next page and APPEND to the existing list
  // instead of replacing it (unlike nextPage, which is used by the classic
  // Prev/Next pagination UI and replaces the list).
  //
  // PERF FIX 2026-08-08 (Gap: scroll gets progressively slower/"stuck" the deeper you go):
  // Audited how Roundcube/Dovecot serve mailboxes this fast on the source server -- they
  // never use OFFSET-style "skip N rows" pagination at all; they seek directly to "the next
  // messages after the last one I already have" via Dovecot's own index. Classic `page`-based
  // OFFSET pagination against Postgres has to scan/discard every prior row, so it measurably
  // got slower the deeper a user scrolled (55ms at page 1 -> 245ms at page 148, measured).
  // Now that the backend supports an optional cursor (cursorDate + cursorId of the last email
  // already loaded), infinite-scroll uses that instead of `page` whenever there's no active
  // search (search/thread-grouping keep the original page-based path -- see emails.service.ts).
  // This makes each scroll-triggered fetch cost the same regardless of how far the user has
  // scrolled, matching the architecture that makes Roundcube/Dovecot feel instant at any depth.
  loadMoreEmails: async () => {
    const { page, hasMore, isLoading, isLoadingMore, currentFolder, limit, emails, searchQuery, nextCursor } = get();
    if (!hasMore || isLoading || isLoadingMore) return;

    set({ isLoadingMore: true });
    try {
      const nextPageNum = page + 1;
      const params: Record<string, any> = {
        limit,
        folder: currentFolder,
      };
      // Prefer the backend-provided opaque cursor (Stalwart returns a numeric offset;
      // Postgres returns a keyset cursor). Only fall back to deriving a keyset cursor from
      // the last email's UUID when the backend gave us no cursor at all (Postgres path).
      if (nextCursor) {
        params.cursorId = nextCursor;
      } else if (!searchQuery && emails.length > 0) {
        const lastEmail = emails[emails.length - 1];
        const cursorDate = lastEmail.receivedAt || lastEmail.date;
        params.cursorDate = new Date(cursorDate).toISOString();
        params.cursorId = lastEmail.id;
      } else {
        params.page = nextPageNum;
      }
      const response = await api.get('/emails', { params });

      // Stale-response guard: bail if the user navigated to a different folder mid-flight
      if (get().currentFolder !== currentFolder) {
        set({ isLoadingMore: false });
        return;
      }

      const data = response.data?.data || response.data;
      const newEmails = Array.isArray(data) ? data : (data.data || data.emails || []);
      // Cursor responses return meta.total = -1 (sentinel: "not recomputed, keep existing")
      // since redoing COUNT(*) on every scroll tick is itself needless overhead once we
      // already know the total from the initial page-1 load.
      const rawTotal = response.data?.pagination?.total ?? data.meta?.total ?? data.total;
      const total = (rawTotal === undefined || rawTotal === -1) ? get().total : rawTotal;
      // Capture the backend's next cursor (Stalwart returns a numeric offset string).
      const backendNextCursor = data.nextCursor ?? response.data?.nextCursor ?? null;

      // De-dupe in case of overlap (e.g. a new email shifted pagination)
      const existingIds = new Set(emails.map((e: any) => e.id));
      const appended = [...emails, ...newEmails.filter((e: any) => !existingIds.has(e.id))];
      newEmails.forEach((e: any) => _knownEmailIds.add(e.id));

      startTransition(() => {
        set({
          emails: appended,
          page: nextPageNum,
          total,
          hasMore: newEmails.length === limit,
          nextCursor: backendNextCursor,
          isLoadingMore: false,
        });
      });
    } catch (error) {
      console.error('[loadMoreEmails] Failed:', error);
      set({ isLoadingMore: false });
    }
  },


  // PERF FIX 2026-08-08 (Gap #1 - "click shows no response, takes long" after scrolling):
  // this was wrapped in startTransition, marking it as LOW-PRIORITY -- exactly the same
  // priority as loadMoreEmails' list-append update below. When a user clicked an email
  // shortly after scrolling (while loadMoreEmails' transition was still being processed/
  // committed), React's concurrent scheduler could keep the click's update pending behind
  // the other low-priority transition, delaying the email from opening by several seconds
  // even though the backend responded in under 300ms (confirmed via live testing: DB/API
  // was never the bottleneck, this scheduling priority was). Opening an email in direct
  // response to a click is an URGENT interaction and must never be deprioritized behind
  // background list updates -- so this is now a plain, synchronous, high-priority update.
  setSelectedEmail: (email) => set({ selectedEmail: email }),
  
  toggleEmailSelection: (id) => {
    const selected = get().selectedEmails;
    if (selected.includes(id)) {
      set({ selectedEmails: selected.filter(i => i !== id) });
    } else {
      set({ selectedEmails: [...selected, id] });
    }
  },
  
  selectAllEmails: () => {
    set({ selectedEmails: get().emails.map(e => e.id) });
  },
  
  clearSelection: () => set({ selectedEmails: [] }),

  markAsRead: async (ids, isRead) => {
    try {
      // Backend uses individual PUT requests for each email
      await Promise.all(
        ids.map(id => api.put(`/emails/${id}/${isRead ? 'read' : 'unread'}`))
      );
      set({
        emails: get().emails.map(e =>
          ids.includes(e.id) ? { ...e, isRead } : e
        ),
        selectedEmail: get().selectedEmail && ids.includes(get().selectedEmail!.id)
          ? { ...get().selectedEmail!, isRead }
          : get().selectedEmail,
      });
      // Refresh folder counts after read/unread change
      get().fetchFolders();
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to update emails' });
    }
  },

  markAsStarred: async (ids, isStarred) => {
    try {
      // Backend uses toggle endpoint for each email
      await Promise.all(ids.map(id => api.put(`/emails/${id}/star`)));
      set({
        emails: get().emails.map(e =>
          ids.includes(e.id) ? { ...e, isStarred } : e
        ),
        selectedEmail: get().selectedEmail && ids.includes(get().selectedEmail!.id)
          ? { ...get().selectedEmail!, isStarred }
          : get().selectedEmail,
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to update emails' });
    }
  },

  markAsImportant: async (ids, isImportant) => {
    try {
      // Backend uses toggle endpoint for each email - toggles flag/important
      await Promise.all(ids.map(id => api.put(`/emails/${id}/important`)));
      set({
        emails: get().emails.map(e =>
          ids.includes(e.id) ? { ...e, isImportant, isFlagged: isImportant } : e
        ),
        selectedEmail: get().selectedEmail && ids.includes(get().selectedEmail!.id)
          ? { ...get().selectedEmail!, isImportant, isFlagged: isImportant }
          : get().selectedEmail,
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to update emails' });
    }
  },

  pinEmail: async (ids, isPinned) => {
    try {
      // Local-only feature - pinned emails are stored in browser state
      // Update the emails and sort pinned to top
      const updatedEmails = get().emails.map(e =>
        ids.includes(e.id) ? { ...e, isPinned } : e
      );
      // Sort: pinned first, then by date
      updatedEmails.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
      set({ emails: updatedEmails });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to pin emails' });
    }
  },

  snoozeEmails: async (ids, until) => {
    try {
      await Promise.all(ids.map(id => api.put(`/emails/${id}/snooze`, { until: until.toISOString() })));
      // Remove snoozed emails from current view (they go to snoozed folder)
      set({
        emails: get().emails.filter(e => !ids.includes(e.id)),
        selectedEmail: get().selectedEmail && ids.includes(get().selectedEmail!.id)
          ? null
          : get().selectedEmail,
        selectedEmails: [],
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to snooze emails' });
    }
  },

  setReminder: async (ids, reminderAt) => {
    try {
      // Local-only feature for now
      set({
        emails: get().emails.map(e =>
          ids.includes(e.id) ? { ...e, reminderAt: reminderAt?.toISOString() } : e
        ),
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to set reminder' });
    }
  },

  setFollowUpTag: async (ids, tag) => {
    try {
      // Local-only feature for now
      set({
        emails: get().emails.map(e =>
          ids.includes(e.id) ? { ...e, followUpTag: tag === 'none' ? undefined : tag } : e
        ),
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to set follow-up tag' });
    }
  },

  moveToFolder: async (ids, folder) => {
    try {
      // Backend uses individual PUT requests
      await Promise.all(
        ids.map(id => api.put(`/emails/${id}/move`, { folder }))
      );
      const prevTotal = get().total ?? 0;
      set({
        emails: get().emails.filter(e => !ids.includes(e.id)),
        total: Math.max(0, prevTotal - ids.length),
        selectedEmail: get().selectedEmail && ids.includes(get().selectedEmail!.id)
          ? null
          : get().selectedEmail,
        selectedEmails: [],
      });
      // Refresh folder counts after moving
      get().fetchFolders();
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to move emails' });
    }
  },

  markAsSpam: async (ids) => {
    try {
      if (ids.length === 1) {
        await api.post(`/emails/${ids[0]}/spam`);
      } else {
        await api.post('/emails/bulk/spam', { ids });
      }
      set({
        emails: get().emails.filter(e => !ids.includes(e.id)),
        selectedEmail: get().selectedEmail && ids.includes(get().selectedEmail!.id)
          ? null
          : get().selectedEmail,
        selectedEmails: [],
      });
      // Refresh folder counts after spam action
      get().fetchFolders();
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to mark as spam' });
    }
  },

  markAsNotSpam: async (ids) => {
    try {
      if (ids.length === 1) {
        await api.post(`/emails/${ids[0]}/not-spam`);
      } else {
        await api.post('/emails/bulk/not-spam', { ids });
      }
      set({
        emails: get().emails.filter(e => !ids.includes(e.id)),
        selectedEmail: get().selectedEmail && ids.includes(get().selectedEmail!.id)
          ? null
          : get().selectedEmail,
        selectedEmails: [],
      });
      // Refresh folder counts after not-spam action
      get().fetchFolders();
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to mark as not spam' });
    }
  },

  deleteEmails: async (ids) => {
    try {
      const currentFolder = get().currentFolder;
      if (currentFolder === 'trash') {
        // Permanent delete - use individual DELETE requests
        await Promise.all(ids.map(id => api.delete(`/emails/${id}`)));
      } else {
        // Move to trash
        await get().moveToFolder(ids, 'trash');
        return;
      }
      set({
        emails: get().emails.filter(e => !ids.includes(e.id)),
        selectedEmail: get().selectedEmail && ids.includes(get().selectedEmail!.id)
          ? null
          : get().selectedEmail,
        selectedEmails: [],
      });
      // Refresh folder counts after delete
      get().fetchFolders();
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to delete emails' });
    }
  },

  addLabel: async (ids, labelId) => {
    try {
      // Persist the label on the backend (Stalwart keyword "$label-<name>").
      // This survives restarts — it is NOT just local state.
      await Promise.all(
        ids.map((id) => api.put(`/emails/${id}/label/${encodeURIComponent(labelId)}`)),
      );
      // Update local state to reflect the change immediately.
      set({
        emails: get().emails.map(e =>
          ids.includes(e.id) && !e.labels.includes(labelId)
            ? { ...e, labels: [...e.labels, labelId] }
            : e
        ),
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to add label' });
    }
  },

  removeLabel: async (ids, labelId) => {
    try {
      // Persist the label removal on the backend (removes "$label-<name>" keyword).
      await Promise.all(
        ids.map((id) => api.delete(`/emails/${id}/label/${encodeURIComponent(labelId)}`)),
      );
      // Update local state to reflect the change immediately.
      set({
        emails: get().emails.map(e =>
          ids.includes(e.id)
            ? { ...e, labels: e.labels.filter(l => l !== labelId) }
            : e
        ),
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to remove label' });
    }
  },

  sendEmail: async (email) => {
    set({ isLoading: true });
    try {
      // Transform EmailAddress objects to plain strings for backend
      const toAddresses = (Array.isArray(email.to) ? email.to : []).map((r: any) => typeof r === 'string' ? r : r.address);
      const ccAddresses = email.cc ? (email.cc as any[]).map((r: any) => typeof r === 'string' ? r : r.address) : undefined;
      const bccAddresses = email.bcc ? (email.bcc as any[]).map((r: any) => typeof r === 'string' ? r : r.address) : undefined;

      const payload = {
        to: toAddresses,
        cc: ccAddresses,
        bcc: bccAddresses,
        subject: email.subject,
        html: email.htmlBody || email.body,
        text: email.textBody,
        preheader: email.preheader,
        inReplyTo: email.inReplyTo,
      };

      // Check if there are attachments
      if (email.attachments && email.attachments.length > 0) {
        // Use FormData for emails with attachments
        const formData = new FormData();
        formData.append('emailData', JSON.stringify(payload));
        
        // Add attachment files
        email.attachments.forEach((att: any) => {
          if (att.file) {
            formData.append('attachments', att.file);
          }
        });
        
        await api.post('/mail/send', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // No attachments - send as JSON
        await api.post('/mail/send', payload);
      }
      set({ isLoading: false, isComposeOpen: false, draftEmail: null });
    } catch (error: any) {
      console.error('[sendEmail] FAILED:', error);
      console.error('[sendEmail] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
        }
      });
      set({
        error: error.response?.data?.message || 'Failed to send email',
        isLoading: false,
      });
      throw error;
    }
  },

  saveDraft: async (email) => {
    try {
      const response = await api.post('/emails/draft', email);
      const draft = response.data?.data || response.data;
      set({ draftEmail: draft });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to save draft' });
    }
  },

  // Schedule Email Implementation
  scheduleEmail: async (email, scheduledAt) => {
    set({ isLoading: true });
    try {
      const toAddresses = Array.isArray(email.to) 
        ? email.to.map((t: any) => typeof t === 'string' ? t : t.address)
        : [email.to];
      const ccAddresses = email.cc 
        ? (Array.isArray(email.cc) ? email.cc.map((c: any) => typeof c === 'string' ? c : c.address) : [email.cc])
        : undefined;
      const bccAddresses = email.bcc
        ? (Array.isArray(email.bcc) ? email.bcc.map((b: any) => typeof b === 'string' ? b : b.address) : [email.bcc])
        : undefined;

      const response = await api.post('/mail/scheduled', {
        toAddresses,
        ccAddresses,
        bccAddresses,
        subject: email.subject,
        htmlBody: email.htmlBody || email.body,
        textBody: email.textBody,
        scheduledAt: scheduledAt.toISOString(),
        inReplyTo: email.inReplyTo,
        attachments: email.attachments,
      });
      
      const scheduled = response.data?.data || response.data;
      set({ 
        scheduledEmails: [...get().scheduledEmails, scheduled],
        isLoading: false, 
        isComposeOpen: false, 
        draftEmail: null 
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Failed to schedule email',
        isLoading: false,
      });
      throw error;
    }
  },

  fetchScheduledEmails: async () => {
    try {
      const response = await api.get('/mail/scheduled');
      const scheduled = response.data?.data || response.data || [];
      set({ scheduledEmails: scheduled });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to fetch scheduled emails' });
    }
  },

  cancelScheduledEmail: async (id) => {
    try {
      await api.post(`/mail/scheduled/${id}/cancel`);
      set({
        scheduledEmails: get().scheduledEmails.map(e =>
          e.id === id ? { ...e, status: 'cancelled' as const } : e
        ),
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to cancel scheduled email' });
    }
  },

  updateScheduledEmail: async (id, data) => {
    try {
      const response = await api.put(`/mail/scheduled/${id}`, data);
      const updated = response.data?.data || response.data;
      set({
        scheduledEmails: get().scheduledEmails.map(e =>
          e.id === id ? { ...e, ...updated } : e
        ),
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to update scheduled email' });
    }
  },

  // Template Actions
  fetchTemplates: async () => {
    try {
      const response = await api.get('/mail/templates');
      const templates = response.data?.data || response.data || [];
      set({ templates });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to fetch templates' });
    }
  },

  createTemplate: async (template) => {
    try {
      const response = await api.post('/mail/templates', template);
      const created = response.data?.data || response.data;
      set({ templates: [...get().templates, created] });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to create template' });
    }
  },

  updateTemplate: async (id, template) => {
    try {
      const response = await api.put(`/mail/templates/${id}`, template);
      const updated = response.data?.data || response.data;
      set({
        templates: get().templates.map(t => t.id === id ? { ...t, ...updated } : t),
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to update template' });
    }
  },

  deleteTemplate: async (id) => {
    try {
      await api.delete(`/mail/templates/${id}`);
      set({ templates: get().templates.filter(t => t.id !== id) });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to delete template' });
    }
  },

  // Signature Actions
  fetchSignatures: async () => {
    try {
      const response = await api.get('/mail/signatures');
      const signatures = response.data?.data || response.data || [];
      const defaultSig = signatures.find((s: EmailSignature) => s.isDefault) || null;
      set({ signatures, defaultSignature: defaultSig });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to fetch signatures' });
    }
  },

  createSignature: async (signature) => {
    try {
      const response = await api.post('/mail/signatures', signature);
      const created = response.data?.data || response.data;
      const newSignatures = [...get().signatures, created];
      set({ 
        signatures: newSignatures,
        defaultSignature: created.isDefault ? created : get().defaultSignature,
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to create signature' });
    }
  },

  updateSignature: async (id, signature) => {
    try {
      const response = await api.put(`/mail/signatures/${id}`, signature);
      const updated = response.data?.data || response.data;
      const newSignatures = get().signatures.map(s => s.id === id ? { ...s, ...updated } : s);
      const newDefault = updated.isDefault ? updated : 
        (get().defaultSignature?.id === id && !updated.isDefault ? null : get().defaultSignature);
      set({ signatures: newSignatures, defaultSignature: newDefault });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to update signature' });
    }
  },

  deleteSignature: async (id) => {
    try {
      await api.delete(`/mail/signatures/${id}`);
      const newSignatures = get().signatures.filter(s => s.id !== id);
      const newDefault = get().defaultSignature?.id === id ? null : get().defaultSignature;
      set({ signatures: newSignatures, defaultSignature: newDefault });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to delete signature' });
    }
  },

  setDefaultSignature: async (id) => {
    try {
      await api.post(`/mail/signatures/${id}/default`);
      const signatures = get().signatures.map(s => ({
        ...s,
        isDefault: s.id === id,
      }));
      const newDefault = signatures.find(s => s.id === id) || null;
      set({ signatures, defaultSignature: newDefault });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to set default signature' });
    }
  },

  getSignatureForAddress: async (address) => {
    try {
      const response = await api.get('/mail/signatures/for-address', {
        params: { address },
      });
      return response.data?.data || response.data || null;
    } catch {
      return get().defaultSignature;
    }
  },

  // Vacation Responder Actions
  fetchVacationResponder: async () => {
    try {
      const response = await api.get('/mail/vacation');
      const responder = response.data?.data || response.data || null;
      set({ vacationResponder: responder });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to fetch vacation responder' });
    }
  },

  updateVacationResponder: async (data) => {
    try {
      const response = await api.post('/mail/vacation', data);
      const updated = response.data?.data || response.data;
      set({ vacationResponder: updated });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to update vacation responder' });
    }
  },

  toggleVacationResponder: async (enabled) => {
    try {
      if (enabled) {
        await api.post('/mail/vacation/enable');
      } else {
        await api.post('/mail/vacation/disable');
      }
      const current = get().vacationResponder;
      if (current) {
        set({ vacationResponder: { ...current, isEnabled: enabled } });
      }
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to toggle vacation responder' });
    }
  },

  // Saved Search Actions
  fetchSavedSearches: async () => {
    try {
      const response = await api.get('/mail/saved-searches');
      const searches = response.data?.data || response.data || [];
      set({ savedSearches: searches });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to fetch saved searches' });
    }
  },

  createSavedSearch: async (search) => {
    try {
      const response = await api.post('/mail/saved-searches', search);
      const created = response.data?.data || response.data;
      set({ savedSearches: [...get().savedSearches, created] });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to create saved search' });
    }
  },

  deleteSavedSearch: async (id) => {
    try {
      await api.delete(`/mail/saved-searches/${id}`);
      set({ savedSearches: get().savedSearches.filter(s => s.id !== id) });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to delete saved search' });
    }
  },

  executeSavedSearch: async (id) => {
    try {
      await api.post(`/mail/saved-searches/${id}/use`);
      const search = get().savedSearches.find(s => s.id === id);
      if (search) {
        set({ 
          searchQuery: search.query || '',
          searchFilters: search.filters || {},
        });
        get().fetchEmails(get().currentFolder, 1);
      }
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to execute saved search' });
    }
  },

  // Reminder Actions
  fetchReminders: async () => {
    try {
      const response = await api.get('/mail/reminders');
      const reminders = response.data?.data || response.data || [];
      set({ reminders });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to fetch reminders' });
    }
  },

  createReminder: async (emailId, remindAt, note) => {
    try {
      const response = await api.post('/mail/reminders', {
        emailId,
        remindAt: remindAt.toISOString(),
        note,
      });
      const created = response.data?.data || response.data;
      set({ reminders: [...get().reminders, created] });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to create reminder' });
    }
  },

  deleteReminder: async (id) => {
    try {
      await api.delete(`/mail/reminders/${id}`);
      set({ reminders: get().reminders.filter(r => r.id !== id) });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to delete reminder' });
    }
  },

  completeReminder: async (id) => {
    try {
      await api.post(`/mail/reminders/${id}/complete`);
      set({
        reminders: get().reminders.map(r =>
          r.id === id ? { ...r, isCompleted: true } : r
        ),
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to complete reminder' });
    }
  },

  setCurrentFolder: (folder) => {
    // Clear search state atomically with folder change so that the fetchEmails
    // call below never runs with a stale searchQuery (which would omit the folder
    // filter and return all-mailbox results instead of the target folder).
    set({ currentFolder: folder, selectedEmail: null, selectedEmails: [], page: 1, searchQuery: '', searchFilters: {} });
    get().fetchEmails(folder, 1);
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query, page: 1 });
    get().fetchEmails(get().currentFolder, 1);
  },

  // Clear search state WITHOUT triggering a fetch — use when navigating folders
  // to avoid a stale-folder fetch racing with the intended folder fetch.
  clearSearch: () => {
    set({ searchQuery: '', searchFilters: {}, page: 1 });
  },

  setSearchScope: (scope) => {
    set({ searchScope: scope });
    if (get().searchQuery) get().fetchEmails(get().currentFolder, 1);
  },

  setSearchSort: (sort) => {
    set({ searchSort: sort });
    if (get().searchQuery) get().fetchEmails(get().currentFolder, 1);
  },

  setIncludeSpamTrash: (v) => {
    set({ includeSpamTrash: v });
    if (get().searchQuery) get().fetchEmails(get().currentFolder, 1);
  },

  setSearchFilters: (filters) => {
    set({ searchFilters: filters, page: 1 });
  },

  setInboxCategory: (category) => {
    set({ inboxCategory: category, page: 1 });
    get().fetchEmails(get().currentFolder, 1);
  },

  setComposeOpen: (isOpen, draft) => {
    set({ isComposeOpen: isOpen, draftEmail: draft || null });
  },

  createFolder: async (name, parentId, color) => {
    try {
      const response = await api.post('/folders', { name, parentId, color });
      let folder = response.data?.data || response.data;
      // Unwrap double nesting from TransformInterceptor
      if (folder && folder.data && !folder.name) folder = folder.data;
      set({ folders: [...get().folders, { ...folder, type: 'custom', unreadCount: 0, totalCount: 0 }] });
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to create folder';
      set({ error: msg });
      throw new Error(msg);
    }
  },

  deleteFolder: async (id) => {
    try {
      await api.delete(`/folders/${id}`);
      set({ folders: get().folders.filter(f => f.id !== id) });
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to delete folder';
      set({ error: msg });
      throw new Error(msg);
    }
  },

  renameFolder: async (id, name, color) => {
    try {
      await api.put(`/folders/${id}`, { name, color });
      set({
        folders: get().folders.map(f =>
          f.id === id ? { ...f, name, ...(color ? { color } : {}) } : f
        ),
      });
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to rename folder';
      set({ error: msg });
      throw new Error(msg);
    }
  },

  createLabel: async (name, color) => {
    try {
      const response = await api.post('/labels', { name, color });
      const label = response.data?.data || response.data;
      set({ labels: [...get().labels, label] });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to create label' });
    }
  },

  deleteLabel: async (id) => {
    try {
      await api.delete(`/labels/${id}`);
      set({ labels: get().labels.filter(l => l.id !== id) });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to delete label' });
    }
  },

  updateLabel: async (id, name, color) => {
    try {
      await api.put(`/labels/${id}`, { name, color });
      set({
        labels: get().labels.map(l =>
          l.id === id ? { ...l, name, color } : l
        ),
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to update label' });
    }
  },
}));
